import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setResolvedUserId, setUserData } from '../../store/slices/userManagementSlice';
import { useLocation } from 'react-router-dom';
import { Alert, Button, CardBody, Col, Container, Input, Progress, Spinner, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import { toast } from 'react-toastify';
import { service, isGroupAccessError, isSessionEndedError } from './service';
import {
  getStoredGroups,
  getSelectedGroupId,
  resolveSelectedGroup,
  selectGroup,
  storeGroups,
  signOut,
  NO_GROUPS_MESSAGE
} from './session';
import { isGroupPortalRole } from './roles';
import MemberFormModal from './MemberFormModal';
import RemoveMemberModal from './RemoveMemberModal';
import './GroupPortal.css';
import Breadcrumb from '../common/Breadcrumb/Breadcrumb'

const STATUS_META = {
  active: { label: 'Active', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  removed: { label: 'Removed', color: 'secondary' },
  expired: { label: 'Expired', color: 'secondary' }
};

const FILTERS = [
  { key: 'current', label: 'Current', statuses: ['active', 'pending'] },
  { key: 'active', label: 'Active', statuses: ['active'] },
  { key: 'pending', label: 'Pending', statuses: ['pending'] },
  { key: 'removed', label: 'Removed', statuses: ['removed', 'expired'] }
];

const ROLE_LABELS = { owner: 'Group Owner', admin: 'Group Admin' };

const formatDate = date => (date ? moment(date).format('DD MMM YYYY') : '—');

// The API still saves when an email fails, and says so in the message.
const notifySaved = message => {
  if (/could not be sent/i.test(message || '')) {
    toast.warning(message, { autoClose: 8000 });
  } else {
    toast.success(message);
  }
};

const MemberList = () => {
  const dispatch = useDispatch();
  const [groups, setGroups] = useState(getStoredGroups);
  const [selectedGroupId, setSelectedGroupId] = useState(getSelectedGroupId);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('current');
  const [search, setSearch] = useState('');
  const [formModal, setFormModal] = useState({ open: false, member: null });
  const [removeModal, setRemoveModal] = useState({ open: false, member: null });
  const [saving, setSaving] = useState(false);
  const [busyMemberId, setBusyMemberId] = useState(null);
  // Responses for a group the user has since switched away from are ignored.
  const loadId = useRef(0);
  const location = useLocation();
  console.log(location, "location1234")
  const redirect =  location.state?.redirect ? true : false;

  useEffect(() => {
    initPortal();
  }, []);

  const endSession = message => {
    toast.error(message);
    setTimeout(signOut, 2000);
  };

  // Loads the groups this login manages and selects one: the stored selection if it's still valid,
  // otherwise the first. After a "no access to this group" error (recovering) it always takes the first.
  const initPortal = async ({ recovering = false } = {}) => {
    // Corporate Groups administrators enter with a group selected from the admin list.
    // Their session does not use the group-owner /corporateGroup/groups endpoint.
    if (!isGroupPortalRole(localStorage.getItem('role_id'))) {
      const list = getStoredGroups();
      const selected = resolveSelectedGroup(list);
      if (!selected) {
        toast.error('Select a corporate group before viewing its members.');
        setLoading(false);
        return;
      }
      setGroups(list);
      selectGroup(selected);
      setSelectedGroupId(selected.group_id);
      await loadData({ allowRecovery: false });
      return;
    }
    const response = await service.getGroups();
    let list = getStoredGroups();
    if (response.success && Array.isArray(response.data)) {
      list = response.data;
      storeGroups(list);
      if (list.length === 0) {
        endSession(NO_GROUPS_MESSAGE);
        return;
      }
    } else if (isSessionEndedError(response)) {
      endSession(response.message);
      return;
    } else {
      showFailure(response);
    }
    setGroups(list);
    const selected = recovering ? list[0] : resolveSelectedGroup(list);
    if (!selected) {
      setLoading(false);
      return;
    }
    selectGroup(selected);
    setSelectedGroupId(selected.group_id);
    await loadData({ allowRecovery: !recovering });
  };

  // Handles errors any portal call can return. Returns true when it took over (session ended or group lost).
  const handleAccessFailure = (response, { allowRecovery = true } = {}) => {
    if (isSessionEndedError(response)) {
      endSession(response.message);
      return true;
    }
    if (isGroupAccessError(response) && allowRecovery) {
      toast.error(response.message);
      setFormModal({ open: false, member: null });
      setRemoveModal({ open: false, member: null });
      setLoading(true);
      initPortal({ recovering: true });
      return true;
    }
    return false;
  };

  const showFailure = response => {
    if (!handleAccessFailure(response)) {
      toast.error(response.message);
    }
  };

  const loadData = async ({ allowRecovery = true } = {}) => {
    const requestId = ++loadId.current;
    const [summaryResponse, membersResponse] = await Promise.all([service.getSummary(), service.getMembers()]);
    if (requestId !== loadId.current) return;
    const failed = [summaryResponse, membersResponse].find(response => !response.success);
    if (failed && handleAccessFailure(failed, { allowRecovery })) return;
    if (summaryResponse.success) {
      setSummary(summaryResponse.data);
    } else {
      toast.error(summaryResponse.message);
    }
    if (membersResponse.success) {
      setMembers(Array.isArray(membersResponse.data) ? membersResponse.data : []);
    } else {
      toast.error(membersResponse.message);
    }
    setLoading(false);
  };

  const handleGroupChange = groupId => {
    const group = groups.find(g => String(g.group_id) === String(groupId));
    if (!group || group.group_id === selectedGroupId) return;
    selectGroup(group);
    setSelectedGroupId(group.group_id);
    setSummary(null);
    setMembers([]);
    setFilter('current');
    setSearch('');
    setLoading(true);
    loadData();
  };

  const groupIsActive = !summary || summary.status === 'active';
  const seatsFull = !!summary && summary.seats_available <= 0;
  const canAdd = !!summary && groupIsActive && !seatsFull;

  const counts = useMemo(() => {
    const result = {};
    FILTERS.forEach(f => {
      result[f.key] = members.filter(m => f.statuses.includes(m.status)).length;
    });
    return result;
  }, [members]);

  const visibleMembers = useMemo(() => {
    const statuses = FILTERS.find(f => f.key === filter).statuses;
    const term = search.trim().toLowerCase();
    return members.filter(
      m =>
        statuses.includes(m.status) &&
        (!term ||
          (m.name || '').toLowerCase().includes(term) ||
          (m.email || '').toLowerCase().includes(term) ||
          String(m.employee_id || '').toLowerCase().includes(term))
    );
  }, [members, filter, search]);

  const handleSaveMember = async payload => {
    const member = formModal.member;
    setSaving(true);
    const response = member ? await service.updateMember(member.member_id, payload) : await service.addMember(payload);
    setSaving(false);
    if (response.success) {
      notifySaved(response.message || (member ? 'Member updated' : 'Member added'));
      setFormModal({ open: false, member: null });
      loadData();
    } else {
      showFailure(response);
    }
  };

  // Removal is immediate; on failure the dialog stays open with the error toast.
  const handleRemove = async () => {
    const member = removeModal.member;
    setSaving(true);
    const response = await service.removeMember(member.member_id);
    setSaving(false);
    if (response.success) {
      toast.success(response.message || 'Member removed');
      setRemoveModal({ open: false, member: null });
      loadData();
    } else {
      showFailure(response);
    }
  };

  const handleResendWelcome = async member => {
    setBusyMemberId(member.member_id);
    const response = await service.resendWelcome(member.member_id);
    setBusyMemberId(null);
    if (response.success) {
      toast.success(response.message || `Welcome email resent to ${member.email}`);
    } else {
      showFailure(response);
    }
  };

  const handleViewMember = member => {
    const userId = member.user_id || member.member_user_id || member.user?.user_id;
    if (!userId) {
      toast.error('User details are not available for this member yet.');
      return;
    }

    const [firstName, ...lastName] = (member.name || '').trim().split(/\s+/);
    const userData = {
      ...member,
      user_id: userId,
      first_name: firstName || member.name || '',
      last_name: lastName.join(' '),
      user_email: member.email || ''
    };
    dispatch(setUserData(userData));
    dispatch(setResolvedUserId(userId));
    localStorage.setItem('group_member_details_user', JSON.stringify(userData));
    window.location.assign('/group/member-details');
  };

  const statusFormatter = status => {
    const meta = STATUS_META[status] || { label: status };
    return <span className={`group-portal-status group-portal-status-${status}`}>{meta.label}</span>;
  };

  const memberFormatter = (name, row) => (
    <div className="group-portal-member">
      <div className="group-portal-member-name">{name}</div>
      <div className="group-portal-muted">{row.email}</div>
    </div>
  );

  const actionIcon = (id, icon, label, onClick, className = '') => (
    <Fragment key={id}>
      <Button
        id={id}
        color="link"
        size="sm"
        className={`px-1 ${className}`}
        disabled={busyMemberId !== null}
        onClick={onClick}
        aria-label={label}
      >
        <FontAwesomeIcon icon={icon} />
      </Button>
      <UncontrolledTooltip target={id}>{label}</UncontrolledTooltip>
    </Fragment>
  );

  // All members can be viewed; only pending and active members can be edited or removed.
  const actionsFormatter = (cell, row) => {
    const id = row.member_id;
    const viewAction = actionIcon(`view-${id}`, 'eye', 'View member details', () => handleViewMember(row));
    if (row.status !== 'pending' && row.status !== 'active') {
      return <div className="group-portal-actions">{viewAction}</div>;
    }
    return (
      <div className="group-portal-actions">
        {viewAction}
        {actionIcon(`edit-${id}`, 'edit', 'Edit', () => setFormModal({ open: true, member: row }))}
        {row.status === 'pending' &&
          actionIcon(`resend-${id}`, 'envelope', 'Resend welcome email', () => handleResendWelcome(row))}
        {actionIcon(`remove-${id}`, 'trash-alt', 'Remove', () => setRemoveModal({ open: true, member: row }), 'text-danger')}
      </div>
    );
  };

  const columns = [
    { dataField: 'name', text: 'Member', classes: 'py-3 align-middle', formatter: memberFormatter, sort: true },
    { dataField: 'employee_id', text: 'Employee ID', classes: 'py-3 align-middle', sort: true, headerStyle: { width: '140px' } },
    {
      dataField: 'status',
      text: 'Status',
      classes: 'py-3 align-middle',
      formatter: statusFormatter,
      formatExtraData: busyMemberId,
      headerStyle: { width: '160px' }
    },
    {
      dataField: 'membership_end_date',
      text: 'End Date',
      classes: 'py-3 align-middle',
      formatter: formatDate,
      sort: true,
      headerStyle: { width: '120px' }
    },
    {
      dataField: 'added_at',
      text: 'Added',
      classes: 'py-3 align-middle',
      formatter: formatDate,
      sort: true,
      headerStyle: { width: '120px' }
    },
    {
      dataField: 'actions',
      isDummyField: true,
      text: 'Actions',
      headerClasses: 'text-center',
      classes: 'py-3 align-middle text-center',
      formatter: actionsFormatter,
      formatExtraData: busyMemberId,
      headerStyle: { width: '120px' }
    }
  ];

  const emptyState = () => {
    const noMembersAtAll = members.length === 0;
    return (
      <div className="group-portal-empty">
        <div className="group-portal-empty-icon">
          <FontAwesomeIcon icon="users" />
        </div>
        <h5>{noMembersAtAll ? 'No members yet' : 'No members match'}</h5>
        <p>
          {noMembersAtAll
            ? 'Add your employees to give them full access to the app. Each member uses one seat.'
            : 'Try a different filter or search term.'}
        </p>
        {noMembersAtAll && canAdd && (
          <Button color="primary" onClick={() => setFormModal({ open: true, member: null })}>
            Add Member
          </Button>
        )}
      </div>
    );
  };

  const seatPercent = summary && summary.seat_count ? Math.round((summary.seats_used / summary.seat_count) * 100) : 0;
  const selectedGroup = groups.find(g => g.group_id === selectedGroupId);
  const groupName = (summary && summary.group_name) || (selectedGroup && selectedGroup.group_name) || 'Members';

  return (
    <Fragment>
      <div className="bread-crumbs-container col-lg-10 mt-0 border-0 fit-max-width">
        <div className="bread-crumbs">
          <div className="heading pl-1 pt-2 ml-0 df group-portal-heading">
            {redirect && <Breadcrumb
              items={
                [
                  { label: "Master" },
                  { label: "Corporate Groups", to: "/corporate-groups/list" },
                ]
              }
            />}
            {groups.length > 1 ? (
              <Input
                type="select"
                aria-label="Group"
                className="group-portal-group-select"
                value={selectedGroupId || ''}
                disabled={saving}
                onChange={({ target }) => handleGroupChange(target.value)}
              >
                {groups.map(group => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_name}
                  </option>
                ))}
              </Input>
            ) : (
              <h1 className="mt-1">{groupName}</h1>
            )}
            {summary && ROLE_LABELS[summary.role] && (
              <span className="group-portal-role">{ROLE_LABELS[summary.role]}</span>
            )}
          </div>
          <div>
            <span id="add-member-button" className="d-inline-block">
              <Button
                color="primary"
                disabled={!canAdd || loading}
                style={!canAdd ? { pointerEvents: 'none' } : undefined}
                onClick={() => setFormModal({ open: true, member: null })}
              >
                ADD MEMBER
              </Button>
            </span>
            {!canAdd && summary && (
              <UncontrolledTooltip target="add-member-button">
                {!groupIsActive ? 'This group is no longer active' : 'All seats are in use'}
              </UncontrolledTooltip>
            )}
          </div>
        </div>
      </div>
      <Container className="body-inner-wrapper devider"></Container>

      <Container className="body-inner-wrapper p-0 border-0">
        <Col sm={12} className="p-0 mb-12">
          <CardBody className="p-3">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
            <Fragment>
            {summary && (
              <div className="group-portal-summary">
                <div className="group-portal-card group-portal-seats">
                  <span className="group-portal-label">Seats used</span>
                  <div className="group-portal-value">
                    {summary.seats_used}
                    <span className="group-portal-value-total"> / {summary.seat_count}</span>
                  </div>
                  <Progress
                    value={seatPercent}
                    className={`group-portal-progress ${seatsFull ? 'is-full' : ''}`}
                    aria-label={`${summary.seats_used} of ${summary.seat_count} seats used`}
                  />
                  <span className="group-portal-muted">
                    {summary.seats_available} {summary.seats_available === 1 ? 'seat' : 'seats'} available
                  </span>
                </div>
                <div className="group-portal-card">
                  <span className="group-portal-label">
                    <span className="group-portal-dot group-portal-dot-active" />
                    Active
                  </span>
                  <div className="group-portal-value">{counts.active}</div>
                  <span className="group-portal-muted">Have access now</span>
                </div>
                <div className="group-portal-card">
                  <span className="group-portal-label">
                    <span className="group-portal-dot group-portal-dot-pending" />
                    Pending
                  </span>
                  <div className="group-portal-value">{counts.pending}</div>
                  <span className="group-portal-muted">Waiting to activate</span>
                </div>
                <div className="group-portal-card">
                  <span className="group-portal-label">Membership</span>
                  <div className="group-portal-date">Since {formatDate(summary.start_date)}</div>
                  <span className="group-portal-muted">
                    {summary.end_date ? `Ends ${formatDate(summary.end_date)}` : 'No end date'}
                  </span>
                </div>
              </div>
            )}

            {summary && !groupIsActive && (
              <Alert color="danger" className="mt-3 mb-0">
                This group is no longer active. You can view members, but you can't add new ones. Contact your provider.
              </Alert>
            )}
            {summary && groupIsActive && seatsFull && (
              <Alert color="warning" className="mt-3 mb-0">
                All {summary.seat_count} seats are in use. Remove a member or contact your provider to add more seats.
              </Alert>
            )}

            <div className="group-portal-toolbar">
              <div className="group-portal-filters" role="tablist">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    type="button"
                    role="tab"
                    aria-selected={filter === f.key}
                    className={`group-portal-filter ${filter === f.key ? 'active' : ''}`}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label} <span className="group-portal-count">{counts[f.key]}</span>
                  </button>
                ))}
              </div>
              <div className="group-portal-search">
                <FontAwesomeIcon icon="search" className="group-portal-search-icon" />
                <Input
                  type="search"
                  aria-label="Search members by name, email or employee ID"
                  placeholder="Search members"
                  title="Search by name, email or employee ID"
                  value={search}
                  onChange={({ target }) => setSearch(target.value)}
                />
              </div>
            </div>

            <div className="table-responsive new-table">
              <BootstrapTable
                bootstrap4
                keyField="member_id"
                data={visibleMembers}
                columns={columns}
                bordered={false}
                noDataIndication={emptyState}
                pagination={visibleMembers.length > 20 ? paginationFactory({ sizePerPage: 20, hideSizePerPage: true }) : undefined}
                classes="table-dashboard table-sm fs--1 border-bottom border-200 mb-0 table-dashboard-th-nowrap"
                rowClasses="table-content-row btn-reveal-trigger border-top border-200"
                headerClasses="table-content-row text-900 border-y border-200"
              />
            </div>
            </Fragment>
            )}
          </CardBody>
        </Col>
      </Container>

      <MemberFormModal
        isOpen={formModal.open}
        member={formModal.member}
        groupEndDate={summary && summary.end_date}
        saving={saving}
        onClose={() => setFormModal({ open: false, member: null })}
        onSubmit={handleSaveMember}
      />
      <RemoveMemberModal
        isOpen={removeModal.open}
        member={removeModal.member}
        groupName={(summary && summary.group_name) || (selectedGroup && selectedGroup.group_name)}
        saving={saving}
        onClose={() => setRemoveModal({ open: false, member: null })}
        onSubmit={handleRemove}
      />
    </Fragment>
  );
};

export default MemberList;
